import { ButtonType } from "@constellation-academy/epic-ui";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "@components/button";
import { useTranslation } from "@i18n/hooks";
import { type eraseAttributionAction_EraseOrderAttributionMutation } from "@relay/eraseAttributionAction_EraseOrderAttributionMutation.graphql";
import { type eraseAttributionAction_OrderFragment$key } from "@relay/eraseAttributionAction_OrderFragment.graphql";
import {
	ERASE_ORDER_ATTRIBUTION_MUTATION,
	ORDER_FRAGMENT,
} from "./erase-attribution-action.graphql";
import { dialogFooterClass, hintClass, wrapperClass } from "./erase-attribution-action.styles";
import { type EraseAttributionActionProps } from "./erase-attribution-action.types";

export const EraseAttributionAction = ({ orderFragmentRef }: EraseAttributionActionProps) => {
	const { t } = useTranslation("common");
	const order = useFragment<eraseAttributionAction_OrderFragment$key>(
		ORDER_FRAGMENT,
		orderFragmentRef,
	);
	const [eraseOrderAttribution, isErasing] =
		useMutation<eraseAttributionAction_EraseOrderAttributionMutation>(
			ERASE_ORDER_ATTRIBUTION_MUTATION,
		);
	const [isDialogVisible, setIsDialogVisible] = useState(false);

	const isUnclaimed = !order.purchaser;

	const handleConfirmOnClick = () => {
		eraseOrderAttribution({
			variables: { input: { orderId: order.id } },
			onCompleted: (response) => {
				setIsDialogVisible(false);
				const count =
					response.Admin.Billing.eraseOrderAttribution?.erasedOrderIds.length ?? 0;
				toast.success(t("order.eraseAttributionSuccessCount", { count }));
			},
			onError: () => {
				toast.error(t("order.eraseAttributionError"));
			},
		});
	};

	return (
		<div className={wrapperClass}>
			<div className={hintClass}>{t("order.eraseAttributionHint")}</div>
			<Button
				variant={ButtonVariant.Error}
				type={ButtonType.Button}
				label={t("order.eraseAttributionTitle")}
				disabled={!isUnclaimed || isErasing}
				onClick={() => setIsDialogVisible(true)}
			/>
			<Dialog
				header={t("order.eraseAttributionTitle")}
				visible={isDialogVisible}
				onHide={() => setIsDialogVisible(false)}
				style={{ width: "32rem" }}
				footer={
					<div className={dialogFooterClass}>
						<Button
							variant={ButtonVariant.Outline}
							type={ButtonType.Button}
							label={t("actions.cancel")}
							disabled={isErasing}
							onClick={() => setIsDialogVisible(false)}
						/>
						<Button
							variant={ButtonVariant.Error}
							type={ButtonType.Button}
							label={t("order.eraseAttributionTitle")}
							disabled={isErasing}
							onClick={handleConfirmOnClick}
						/>
					</div>
				}
			>
				<div>{t("order.confirmEraseAttribution")}</div>
			</Dialog>
		</div>
	);
};
